import { AppFunctionComponent } from "../../types"
import React, { useContext } from "react"
import styled, { css } from "styled-components"
import { PrimaryHeading } from "../../components/common/heading.component"
import Section, {
  SectionArticle,
  SectionHeading,
  SectionImage,
  SectionLayout,
} from "../../components/common/sections/section.component"
import SectionBackgroundWrapper from "../../helpers/sitebuilder-background-wrapper/section-background-wrapper"
import { TextBuilder, TextLayout } from "../../types"
import RichText from "../rich-text/rich-text.resolver"
import { FootnoteContext } from "../../templates/page.template"
import { ScrollAnchor } from "../../components/common/sections/custom/innovation-lab-form/innovation-lab.component"
import { SingleAccordionItem } from "../../ui/components/accordion/accordion-item.component"

export const SectionContainer = styled(Section)<{
  customStyle?: TextBuilder["style"]
}>`
  @media (max-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
    padding: 39px 0;
    ${SectionImage} {
      height: calc(100vw - 40px);
      img {
        height: calc(100vw - 40px);
      }
    }
  }

  ${SingleAccordionItem} {
    border-bottom: 1px solid #c7c7c7;
    border-top: 1px solid #c7c7c7;
    padding-bottom: 24px;
    a {
      h4 {
        font-family: Montserrat;
        font-size: 18px;
        font-style: normal;
        font-weight: 700;
        line-height: 150%;
        padding: 20px 0px 0 0;
      }
    }

    &:nth-child(even) {
      border-top: none;
      border-bottom: none;
    }
  }

  ${SectionHeading} {
    h1 {
      margin-bottom: 0 !important;
    }
  }

  ${SectionArticle} {
    p:first-child {
      margin-top: 20px !important;
    }
    hr {
      height: 0.1px;
      border: 1px solid #000;
    }
  }

  ${({ customStyle }) =>
    customStyle?.lineBorder &&
    css`
      ${SectionArticle} {
        hr {
          height: 0.01px;
          border: none !important;
          border-top: ${customStyle.lineBorder} !important;
        }
      }
    `}

  ${({ customStyle }) =>
    customStyle?.small_text_font &&
    css`
      ${customStyle.small_text_font.desktop &&
      css`
        p {
          padding-bottom: 24px;
          padding-top: 20px;
          margin: 0;

          &:nth-child(1) {
            padding-top: 0;
          }
          strong {
            font-size: ${customStyle.small_text_font.desktop.fontSize};
            line-height: ${customStyle.small_text_font.desktop.lineHeight};
            font-weight: ${customStyle.small_text_font.desktop.fontWeight};
          }
        }
      `}
      ${customStyle.small_text_font.mobile &&
      css`
        @media (max-width: ${({ theme }) =>
            theme.breakpoint.mobileHorizontal}px) {
          p {
            padding-bottom: 20px;
            strong {
              font-size: ${customStyle.small_text_font.mobile.fontSize};
              line-height: ${customStyle.small_text_font.mobile.lineHeight};
              font-weight: ${customStyle.small_text_font.mobile.fontWeight};
              padding-top: 20px;
            }
          }
        }
      `}
    `}

  ${({ customStyle }) =>
    customStyle?.titleFont &&
    css`
      ${SectionArticle} {
        ${PrimaryHeading} {
          font-size: ${customStyle.titleFont.desktop.fontSize ?? "inherit"};
        }
        @media (max-width: ${({ theme }) =>
            theme.breakpoint.tabletHorizontal}px) {
          ${PrimaryHeading} {
            font-size: ${customStyle.titleFont.mobile.fontSize ?? "inherit"};
          }
        }
      }
    `}

  ${({ layout }) =>
    [
      SectionLayout.TextLeftAligned,
      SectionLayout.Text,
      SectionLayout.TextText,
    ].includes(layout as SectionLayout) &&
    css`
      ${PrimaryHeading} {
        /* margin-bottom: 25px; */
      }
    `}

  ${({ customStyle }) =>
    customStyle &&
    css`
      @media (min-width: ${({ theme }) =>
          theme.breakpoint.mobileHorizontal}px) {
        --text-max-width: ${customStyle.text_max_width}px;
      }
    `}

    ${({ customStyle }) =>
    customStyle?.three_inline_buttons &&
    css`
      p {
        display: flex;
        justify-content: space-around;
      }
    `}

    ${({ customStyle }) =>
    customStyle?.color &&
    css`
      h1,
      p {
        color: ${customStyle.color};
      }
    `}

    ${({ customStyle }) =>
    customStyle?.full_width &&
    css`
      width: 100%;
    `}

    ${({ customStyle }) =>
    customStyle?.innerBackground &&
    css`
      padding: 64px 0;
      padding-left: 8%;
      padding-right: 8%;
      background: ${customStyle.innerBackground};
    `}
`

const TextResolver: AppFunctionComponent<TextBuilder> = (
  section: TextBuilder
) => {
  const footnotesOptions = useContext(FootnoteContext)

  const resolveLayout = () => {
    switch (section.layout) {
      case TextLayout.LeftAligned:
        return SectionLayout.TextLeftAligned
      case TextLayout.Centered:
        return SectionLayout.Text
      case TextLayout.TwoTexts:
        return SectionLayout.TextText
      case TextLayout.CenteredTwoTexts:
        return SectionLayout.CenteredTwoTexts
      case TextLayout.TextsWithLeftBackground:
        return SectionLayout.TextTextLeftBackground
      case TextLayout.TextsWithRightBackground:
        return SectionLayout.TextTextRightBackground
      default:
        return SectionLayout.Text
    }
  }

  return (
    <SectionBackgroundWrapper customStyle={section.style}>
      <SectionContainer
        layout={resolveLayout()}
        customStyle={section.style}
        as="div"
      >
        <ScrollAnchor id={section.anchorId} />
        {section.title && (
          <SectionHeading>
            <PrimaryHeading>{section.title}</PrimaryHeading>
          </SectionHeading>
        )}
        {section.text && (
          <SectionArticle>
            <RichText content={section.text} options={footnotesOptions} />
          </SectionArticle>
        )}
        {section.secondText && (
          <SectionArticle area={"SecondArticle"}>
            <RichText content={section.secondText} options={footnotesOptions} />
          </SectionArticle>
        )}
      </SectionContainer>
    </SectionBackgroundWrapper>
  )
}

export default TextResolver
